import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { IconDatabase, IconPlus } from '../components/icons'
import RequestError from '../components/RequestError'
import StatusBadge, { STRIPE, toneFor } from '../components/StatusBadge'
import { Button, CardSkeleton, EmptyState, Field } from '../components/ui'
import { api } from '../lib/api'
import { useLiveRefresh } from '../lib/events'
import { planPrice, relativeTime } from '../lib/format'
import { useToast } from '../lib/toast'
import type { DatabaseEngine, PlanTier } from '../lib/types'
import { useApi } from '../lib/useApi'

const ENGINE_LABEL: Record<DatabaseEngine, string> = {
  postgres: 'PostgreSQL',
  redis: 'Redis',
  minio: 'Object storage',
  rabbitmq: 'Message queue',
}

export default function Databases() {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('')
  const [creating, setCreating] = useState(false)
  const { data, error, loading, reload } = useApi(() => api.databases(), [], 60_000)
  useLiveRefresh(reload, (event) => event.kind === 'database')

  const visible = data?.filter(item => `${item.name} ${item.status}`.toLowerCase().includes(query.toLowerCase()) && (!filter || item.status === filter))
  return (
    <>
      <header className="page-head">
        <div className="titles">
          <h1>Data &amp; Storage</h1>
          <p className="subtitle">
            PostgreSQL, Redis and S3-compatible object storage — provisioned in a click, on the same NVMe as your apps.
          </p>
        </div>
        {!creating && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <IconPlus className="" size={16} />
            New database
          </Button>
        )}
      </header>

      {error && <div className="alert error">{error}</div>}
      {data && data.length > 0 && <div className="resource-toolbar">
        <input type="search" aria-label="Search data resources" placeholder="Search by name or status…" value={query} onChange={event => setQuery(event.target.value)} />
        <select aria-label="Filter by status" value={filter} onChange={event => setFilter(event.target.value)}><option value="">All statuses</option>{[...new Set(data.map(item => item.status))].map(status => <option key={status} value={status}>{status}</option>)}</select>
        <span className="resource-count">{visible?.length} of {data.length} data resources</span>
      </div>}
      {data && data.length > 0 && visible?.length === 0 && <EmptyState title="No matches"><span>Try another name or clear the status filter.</span></EmptyState>}


      {creating && (
        <CreateDatabase
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            reload()
          }}
        />
      )}

      {loading && !data && (
        <div className="grid">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      )}

      {data?.length === 0 && !creating && (
        <EmptyState
          icon={<IconDatabase className="empty-art" size={46} />}
          title="No databases yet"
          action={
            <Button variant="primary" onClick={() => setCreating(true)}>
              <IconPlus className="" size={16} />
              New database
            </Button>
          }
        >
          Create one and link it to a service — TalariCloud injects the connection
          string, so a password never lands in a config file.
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <div className="grid">
          {visible?.map((database) => (
            <Link
              key={database.id}
              className="card card-link"
              to={`/databases/${database.id}`}
              style={
                { '--stripe': STRIPE[toneFor(database.status)] } as React.CSSProperties
              }
            >
              <div className="card-head">
                <h2>{database.name}</h2>
                <StatusBadge status={database.status} />
              </div>

              <p className="tiny muted">
                {ENGINE_LABEL[database.engine]} {database.version} ·{' '}
                {database.plan} · {database.disk_size_gb} GB
              </p>

              {database.error_message && (
                <p className="tiny" style={{ marginTop: 8, color: 'var(--danger)' }}>
                  {database.error_message}
                </p>
              )}

              <p className="tiny faint" style={{ marginTop: 12 }}>
                {database.linked_services.length > 0
                  ? `linked to ${database.linked_services.join(', ')}`
                  : 'not linked to a service'}{' '}
                · created {relativeTime(database.created_at)}
              </p>
            </Link>
          ))}
        </div>
      )}
    </>
  )
}

const ENGINE_CHOICES: {
  value: DatabaseEngine
  icon: string
  name: string
  description: string
}[] = [
  {
    value: 'postgres',
    icon: '🐘',
    name: 'PostgreSQL 16',
    description: 'Relational SQL — the default choice. Linked apps get DATABASE_URL.',
  },
  {
    value: 'redis',
    icon: '⚡',
    name: 'Redis 7',
    description: 'In-memory cache, sessions and queues. Linked apps get REDIS_URL.',
  },
  {
    value: 'minio',
    icon: '🪣',
    name: 'Object storage',
    description: 'S3-compatible files and uploads. Linked apps get S3_* credentials.',
  },
  {
    value: 'rabbitmq',
    icon: '🐇',
    name: 'Message queue',
    description: 'RabbitMQ / AMQP broker for background jobs. Linked apps get AMQP_URL.',
  },
]

function CreateDatabase({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [engine, setEngine] = useState<DatabaseEngine>('postgres')
  const [version, setVersion] = useState('')
  const [plan, setPlan] = useState<PlanTier>('starter')
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()

  const { data: plans, error: plansError, reload: retryPlans } = useApi(() => api.databasePlans(), [])
  const { data: engines, error: enginesError, reload: retryEngines } = useApi(() => api.databaseEngines(), [])
  const engineVersions = engines?.find(item => item.engine === engine)
  const selectedVersion = version || engineVersions?.default_version || ''
  const validVersion = !!engineVersions?.versions.includes(selectedVersion) && !enginesError

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!plans?.some(item => item.tier === plan) || plansError || !validVersion) return
    setBusy(true)
    const ok = await attempt(
      () => api.createDatabase({ name, engine, plan, version: selectedVersion }),
      { success: `${name} is provisioning`, failure: 'Could not create the database' },
    )
    setBusy(false)
    if (ok) onCreated()
  }

  return (
    <form className="card card-pad-lg stack" onSubmit={submit}>
      <h2>New data resource</h2>
      {plansError && <RequestError message={plansError} retry={retryPlans} />}
      {enginesError && <RequestError message={enginesError} retry={retryEngines} />}

      <Field label="Engine">
        <div className="engine-grid" role="radiogroup" aria-label="Engine">
          {ENGINE_CHOICES.map((choice) => (
            <button
              key={choice.value}
              type="button"
              role="radio"
              aria-checked={engine === choice.value}
              className="engine-card"
              onClick={() => { setEngine(choice.value); setVersion('') }}
            >
              <span className="engine-icon" aria-hidden="true">
                {choice.icon}
              </span>
              <span className="engine-name">{choice.name}</span>
              <span className="engine-desc">{choice.description}</span>
            </button>
          ))}
        </div>
      </Field>

      <Field label="Version" htmlFor="db-version" hint="Choose the version for this new database.">
        <select id="db-version" value={selectedVersion} disabled={!engineVersions || !!enginesError}
          onChange={event => setVersion(event.target.value)}>
          {!engineVersions && <option value="">Loading versions…</option>}
          {engineVersions?.versions.map(item => (
            <option key={item} value={item}>{item}{item === engineVersions.default_version ? ' (default)' : ''}</option>
          ))}
        </select>
      </Field>

      <Field label="Name" htmlFor="db-name">
        <input
          id="db-name"
          required
          autoFocus
          placeholder={
            engine === 'minio' ? 'uploads' : engine === 'rabbitmq' ? 'events' : 'production-db'
          }
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>

      <Field
        label="Size"
        htmlFor="db-plan"
        hint="The instance is private to your account — there is no public port."
      >
        <select
          id="db-plan"
          value={plan}
          onChange={(event) => setPlan(event.target.value as PlanTier)}
        >
          {(plans ?? []).map((item) => (
            <option key={item.tier} value={item.tier}>
              {item.label} — {item.cpu} CPU, {item.memory} RAM, {item.storage_gb} GB ·{' '}
              {Number(item.monthly_etb) === 0 ? 'free' : `${planPrice(item)}/mo`}
            </option>
          ))}
        </select>
      </Field>

      <div className="row">
        <Button variant="primary" type="submit" loading={busy} disabled={!name.trim() || !plans?.some(item => item.tier === plan) || !!plansError || !validVersion}>
          Create database
        </Button>
        <Button variant="ghost" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

